import { useLayoutEffect, useMemo } from "react";

import { useAuth } from "context/auth-context";
import { SchoolBranding } from "types/api";
import {
  buildSchoolThemeVariables,
  clearSchoolBrandThemeOverrides,
  SchoolThemeVariableMap,
} from "utils/school-theme";

type UseSchoolBrandThemeOptions = {
  enabled?: boolean;
};

const applySchoolBrandTheme = (themeVariables: SchoolThemeVariableMap) => {
  const root = document.documentElement;

  Object.entries(themeVariables).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
};

export const useSchoolBrandTheme = (
  branding?: SchoolBranding | null,
  options: UseSchoolBrandThemeOptions = {},
) => {
  const { schoolThemePreference } = useAuth();
  const enabled = options.enabled ?? true;
  const themeVariables = useMemo(
    () => (schoolThemePreference === "school_brand" ? buildSchoolThemeVariables(branding) : null),
    [branding, schoolThemePreference],
  );

  useLayoutEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    if (schoolThemePreference !== "school_brand" || !enabled) {
      clearSchoolBrandThemeOverrides();
      return undefined;
    }

    if (!themeVariables) {
      return undefined;
    }

    applySchoolBrandTheme(themeVariables);
    return undefined;
  }, [enabled, schoolThemePreference, themeVariables]);
};
